import { router, useFocusEffect } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ArrowRight, Search as SearchIcon } from "lucide-react-native";

import {
  listenDirectoryBusinesses,
  type DirectoryBusiness as Business,
} from "@/lib/businesses/directorySearch";
import {
  buildSmartDirectorySearchQuery,
  parseSearchIntent,
  shouldUseSmartSearch,
  type SearchIntent,
} from "@/lib/search/aiQueryIntent";
import PublicMenu from "@/components/public/PublicMenu";
import { getPublicBottomBarPadding } from "@/components/public/PublicBottomBar";
import {
  getDirectoryResultsSession,
  setDirectoryResultsSession,
  updateDirectoryResultsScrollOffset,
} from "@/lib/directory/resultsSession";
import {
  getPublicAreasByCity,
  getPublicCities,
} from "@/lib/locations/publicLocationFilters";
import type { DirectoryArea, DirectoryCity } from "@/types/location";

const LIST_BUSINESS_INTRO =
  "Soko Grid is a business directory and online storefront for product and service providers in Kenya. " +
  "The platform is built to support physical shops, freelancers, and online businesses. " +
  "Customers can view your business, products, services, location, contact details, website link, social media links, QR code, and WhatsApp details from one simple public profile.";

const LISTING_VALUE_ITEMS = [
  "Be found online",
  "Showcase products and services",
  "Make contact easy",
  "Share your business link",
];

const PROFILE_INCLUDE_ITEMS = [
  "Business name and logo/profile photo",
  "Products and services",
  "Location and Google Maps",
  "Phone and contact details",
  "Website and social media links",
  "WhatsApp details",
  "QR code",
];

const DIRECTORY_TEXT_SEPARATOR = " | ";

const DIRECTORY_MOBILE_INTRO =
  "Soko Grid is a business directory and online storefront for product and service providers in Kenya. " +
  "The platform is built to support physical shops, freelancers, and online businesses to showcase their products and services in the digital market.";

const SMART_SEARCH_DEBOUNCE_MS = 1500;
const MIN_SMART_SEARCH_LENGTH = 8;
const MIN_SMART_SEARCH_WORDS = 2;

/* ------------------------------------------------------------------ */
/* Helpers */
/* ------------------------------------------------------------------ */

function useDebouncedValue<T>(value: T, delay = 350) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);

    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

function normalizeText(value: unknown) {
  return String(value ?? "").trim().toLowerCase();
}

function getNormalizedSearchWords(value: string): string[] {
  return normalizeText(value).split(/\s+/).filter(Boolean);
}

function canTriggerSmartSearch(query: string): boolean {
  const normalized = normalizeText(query);
  const words = getNormalizedSearchWords(normalized);

  return (
    normalized.length >= MIN_SMART_SEARCH_LENGTH &&
    words.length >= MIN_SMART_SEARCH_WORDS &&
    shouldUseSmartSearch(query)
  );
}

function normalizeSubCategory(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((s) => String(s ?? "").trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  return [];
}

function normalizeCategories(business: Business): string[] {
  const set = new Set<string>();

  const primary = String(
    business.categoryName || business.category || ""
  ).trim();

  if (primary) set.add(primary);

  if (Array.isArray(business.categories)) {
    business.categories.forEach((category) => {
      const trimmed = String(category ?? "").trim();
      if (trimmed) set.add(trimmed);
    });
  }

  return Array.from(set);
}

function getBusinessDisplayName(business: Business) {
  return (
    String(business.businessName || business.name || "Business").trim() ||
    "Business"
  );
}

function getBusinessCategoryLine(business: Business) {
  const category = String(
    business.categoryName || business.category || ""
  ).trim();

  const subcategories = normalizeSubCategory(
    business.subcategoryName ?? business.subCategory
  );

  const subcategory = subcategories[0] || "";

  return [category, subcategory].filter(Boolean).join(DIRECTORY_TEXT_SEPARATOR);
}

function getBusinessLocationLine(business: Business) {
  const area = String(business.areaName || "").trim();
  const city = String(business.cityName || "").trim();

  return [area, city].filter(Boolean).join(DIRECTORY_TEXT_SEPARATOR);
}

function getBusinessLogo(business: Business) {
  return business.logoUrl || "https://via.placeholder.com/80?text=LOGO";
}

/* ------------------------------------------------------------------ */
/* Memoized Card */
/* ------------------------------------------------------------------ */

const DirectoryCard = React.memo(function DirectoryCard({
  business,
  onOpen,
}: {
  business: Business;
  onOpen: (business: Business) => void;
}) {
  const businessName = getBusinessDisplayName(business);
  const categoryLine = getBusinessCategoryLine(business);
  const locationLine = getBusinessLocationLine(business);

  return (
    <Pressable
      onPress={() => onOpen(business)}
      style={styles.cardBtn}
      accessibilityRole="button"
      accessibilityLabel={`Open ${businessName} card`}
    >
      <View style={styles.row}>
        <Image source={{ uri: getBusinessLogo(business) }} style={styles.logo} />

        <View style={styles.cardContent}>
          <Text style={styles.name} numberOfLines={1}>
            {businessName}
          </Text>

          <Text style={styles.meta} numberOfLines={1}>
            {categoryLine || "Business"}
          </Text>

          <Text style={styles.addr} numberOfLines={1}>
            {locationLine || "Location not set"}
          </Text>
        </View>
      </View>
    </Pressable>
  );
});

DirectoryCard.displayName = "DirectoryCard";

function ListYourBusinessSidebar({
  onStartListing,
}: {
  onStartListing: () => void;
}) {
  return (
    <>
      <Text style={styles.sideCardTitle}>List your business on Soko Grid</Text>
      <Text style={styles.sideCardText}>{LIST_BUSINESS_INTRO}</Text>

      <View style={styles.sidebarSection}>
        <Text style={styles.sidebarSectionTitle}>Why list on Soko Grid?</Text>
        <View style={styles.compactCardList}>
          {LISTING_VALUE_ITEMS.map((item) => (
            <View key={item} style={styles.compactFeatureItem}>
              <View style={styles.featureDot} />
              <Text style={styles.compactItemText}>{item}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.sidebarSection}>
        <Text style={styles.sidebarSectionTitle}>Your profile can include</Text>
        <View style={styles.checklist}>
          {PROFILE_INCLUDE_ITEMS.map((item) => (
            <View key={item} style={styles.checklistItem}>
              <View style={styles.checkIndicator}>
                <View style={styles.checkIndicatorInner} />
              </View>
              <Text style={styles.checklistText}>{item}</Text>
            </View>
          ))}
        </View>
      </View>

      <Pressable
        accessibilityRole="button"
        onPress={onStartListing}
        style={({ pressed }) => [
          styles.startListingButton,
          pressed ? styles.pressed : null,
        ]}
      >
        <Text style={styles.startListingButtonText}>Start Listing</Text>
        <ArrowRight size={16} color="#ffffff" strokeWidth={2.5} />
      </Pressable>
    </>
  );
}

function FeaturedBusinessesPlaceholder() {
  return (
    <View style={styles.sideCard}>
      <Text style={styles.featuredTitle}>Featured Businesses</Text>
      <Text style={styles.sideCardText}>
        Sponsored and featured businesses will appear here soon.
      </Text>
    </View>
  );
}

type SubmittedSmartSearch = {
  normalizedQuery: string;
  directoryQuery: string;
  intent: SearchIntent;
};

/* ------------------------------------------------------------------ */
/* Inline Filter Row */
/* ------------------------------------------------------------------ */

function InlineFilterRow<T extends { id: string; name: string }>({
  label,
  allLabel,
  items,
  selectedId,
  onSelect,
  loading,
  emptyText,
}: {
  label: string;
  allLabel: string;
  items: T[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  loading: boolean;
  emptyText: string;
}) {
  if (loading) {
    return (
      <View style={styles.inlineFilterBlock}>
        <View style={styles.inlineFilterHeader}>
          <Text style={styles.inlineFilterLabel}>{label}</Text>
          <ActivityIndicator size="small" />
        </View>
      </View>
    );
  }

  if (items.length === 0) {
    return (
      <View style={styles.inlineFilterBlock}>
        <View style={styles.inlineFilterHeader}>
          <Text style={styles.inlineFilterLabel}>{label}</Text>
        </View>

        <Text style={styles.emptyHint}>{emptyText}</Text>
      </View>
    );
  }

  return (
    <View style={styles.inlineFilterBlock}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.inlineFilterScroll}
      >
        <Text style={styles.inlineFilterLabel}>{label}</Text>

        <Pressable
          onPress={() => onSelect(null)}
          style={[
            styles.chip,
            !selectedId ? styles.chipActive : styles.chipInactive,
          ]}
        >
          <Text
            style={[
              styles.chipText,
              !selectedId ? styles.chipTextActive : styles.chipTextInactive,
            ]}
          >
            {allLabel}
          </Text>
        </Pressable>

        {items.map((item) => {
          const active = selectedId === item.id;

          return (
            <Pressable
              key={item.id}
              onPress={() => onSelect(item.id)}
              style={[
                styles.chip,
                active ? styles.chipActive : styles.chipInactive,
              ]}
            >
              <Text
                style={[
                  styles.chipText,
                  active ? styles.chipTextActive : styles.chipTextInactive,
                ]}
                numberOfLines={1}
              >
                {item.name}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* Main Component */
/* ------------------------------------------------------------------ */

export default function DirectoryList() {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const useDesktopLayout = width >= 1080;
  const listBottomPadding = useMemo(
    () => getPublicBottomBarPadding(insets.bottom),
    [insets.bottom]
  );

  const listRef = useRef<FlatList<Business>>(null);
  const scrollOffsetRef = useRef(0);
  const didRestoreRef = useRef(false);
  const smartSearchRequestIdRef = useRef(0);
  const lastSmartSearchParsedQueryRef = useRef<string | null>(null);
  const smartSearchCacheRef = useRef(new Map<string, SubmittedSmartSearch>());

  const initialSessionRef = useRef(getDirectoryResultsSession());
  const initialSession = initialSessionRef.current;
  const searchInputRef = useRef(initialSession?.filters.search ?? "");

  const [rows, setRows] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const [search, setSearch] = useState(initialSession?.filters.search ?? "");
  const [activeCategory, setActiveCategory] = useState(
    initialSession?.filters.activeCategory ?? "All"
  );

  const [selectedCityId, setSelectedCityId] = useState<string | null>(
    initialSession?.filters.selectedCityId ?? null
  );

  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(
    initialSession?.filters.selectedAreaId ?? null
  );

  const [allCities, setAllCities] = useState<DirectoryCity[]>([]);
  const [areasForCity, setAreasForCity] = useState<DirectoryArea[]>([]);

  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [smartSearch, setSmartSearch] = useState<SubmittedSmartSearch | null>(
    null
  );
  const [submittedSearch, setSubmittedSearch] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search, 350);
  const smartDebouncedSearch = useDebouncedValue(
    search,
    SMART_SEARCH_DEBOUNCE_MS
  );

  const normalizedInputSearch = useMemo(
    () => normalizeText(search),
    [search]
  );

  const normalizedSearch = useMemo(
    () => normalizeText(submittedSearch ?? debouncedSearch),
    [debouncedSearch, submittedSearch]
  );

  const activeSmartSearch = useMemo(() => {
    if (!smartSearch) return null;

    return smartSearch.normalizedQuery === normalizedInputSearch
      ? smartSearch
      : null;
  }, [smartSearch, normalizedInputSearch]);

  const smartSearchQuery = useMemo(
    () =>
      activeSmartSearch?.directoryQuery ||
      (submittedSearch ?? debouncedSearch).trim(),
    [activeSmartSearch, debouncedSearch, submittedSearch]
  );

  const smartSearchIntent = activeSmartSearch?.intent ?? null;
  const smartSearchApplied = !!activeSmartSearch;

  const hasSearch = normalizedSearch.length > 0;
  const showCityFilter = hasSearch;
  const showAreaFilter = !!selectedCityId;

  /* ------------------------------------------------------------------ */
  /* Load businesses */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    setLoading(true);

    const unsubscribe = listenDirectoryBusinesses(
      {
        search: smartSearchQuery,
        intent: smartSearchIntent,
        cityId: selectedCityId,
        areaId: selectedAreaId,
        limitCount: smartSearchQuery.trim() ? 40 : 60,
      },
      (items) => {
        setRows(items);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [smartSearchQuery, smartSearchIntent, selectedCityId, selectedAreaId]);

  /* ------------------------------------------------------------------ */
  /* Load cities after customer searches */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    let mounted = true;

    async function loadCities() {
      if (!hasSearch) {
        setAllCities([]);
        return;
      }

      try {
        setLoadingCities(true);

        /**
         * Do not pass search text here.
         *
         * Example:
         * Customer searches "plot".
         * We need cities attached to businesses matching "plot",
         * not cities whose names contain "plot".
         */
        const items = await getPublicCities();

        if (mounted) {
          setAllCities(items);
        }
      } catch (error) {
        console.error("Failed to load cities", error);

        if (mounted) {
          setAllCities([]);
        }
      } finally {
        if (mounted) {
          setLoadingCities(false);
        }
      }
    }

    loadCities();

    return () => {
      mounted = false;
    };
  }, [hasSearch]);

  /* ------------------------------------------------------------------ */
  /* Load areas for selected city */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    let mounted = true;

    async function loadAreas() {
      if (!selectedCityId) {
        setAreasForCity([]);
        return;
      }

      const selectedCity =
        allCities.find((city) => city.id === selectedCityId) ?? null;

      try {
        setLoadingAreas(true);

        /**
         * Important:
         * Your Firestore path is:
         *
         * counties/{countyId}/cities/{cityId}/areas/{areaId}
         *
         * So we pass both cityId and countyId when available.
         */
        const items = await getPublicAreasByCity(
          selectedCityId,
          selectedCity?.countyId ?? null
        );

        if (mounted) {
          setAreasForCity(items);
        }
      } catch (error) {
        console.error("Failed to load areas", error);

        if (mounted) {
          setAreasForCity([]);
        }
      } finally {
        if (mounted) {
          setLoadingAreas(false);
        }
      }
    }

    loadAreas();

    return () => {
      mounted = false;
    };
  }, [selectedCityId, allCities]);

  /* ------------------------------------------------------------------ */
  /* Available city chips from current business results */
  /* ------------------------------------------------------------------ */

  const availableCities = useMemo(() => {
    if (!hasSearch) return [];

    const usedCityIds = new Set(
      rows
        .map((business) => business.cityId)
        .filter((value): value is string => typeof value === "string" && !!value)
    );

    return allCities.filter((city) => usedCityIds.has(city.id));
  }, [allCities, rows, hasSearch]);

  /* ------------------------------------------------------------------ */
  /* Available area chips from selected city results */
  /* ------------------------------------------------------------------ */

  const availableAreas = useMemo(() => {
    if (!selectedCityId) return [];

    const usedAreaIds = new Set(
      rows
        .filter((business) => business.cityId === selectedCityId)
        .map((business) => business.areaId)
        .filter((value): value is string => typeof value === "string" && !!value)
    );

    return areasForCity.filter((area) => usedAreaIds.has(area.id));
  }, [areasForCity, rows, selectedCityId]);

  /* ------------------------------------------------------------------ */
  /* Category chips from current business results */
  /* ------------------------------------------------------------------ */

  const categories = useMemo(() => {
    const set = new Set<string>();

    rows.forEach((business) => {
      normalizeCategories(business).forEach((category) => set.add(category));
    });

    return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  useEffect(() => {
    if (!categories.includes(activeCategory)) {
      setActiveCategory("All");
    }
  }, [categories, activeCategory]);

  const filtered = useMemo(() => {
    return rows.filter((business) => {
      const categoriesForBusiness = normalizeCategories(business);

      return (
        activeCategory === "All" ||
        categoriesForBusiness.includes(activeCategory)
      );
    });
  }, [rows, activeCategory]);

  const filteredSlugs = useMemo(() => {
    return filtered
      .map((business) => business.slug)
      .filter((slug): slug is string => typeof slug === "string" && !!slug);
  }, [filtered]);

  /* ------------------------------------------------------------------ */
  /* Save directory session */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    setDirectoryResultsSession({
      slugs: filteredSlugs,
      selectedIndex: initialSession?.selectedIndex ?? 0,
      scrollOffsetY: scrollOffsetRef.current,
      filters: {
        search,
        activeCategory,
        selectedCityId,
        selectedAreaId,
      },
      updatedAt: Date.now(),
    });
  }, [
    filteredSlugs,
    search,
    activeCategory,
    selectedCityId,
    selectedAreaId,
    initialSession?.selectedIndex,
  ]);

  /* ------------------------------------------------------------------ */
  /* Restore scroll when returning from business card */
  /* ------------------------------------------------------------------ */

  useFocusEffect(
    useCallback(() => {
      const session = getDirectoryResultsSession();

      if (!session) return;

      requestAnimationFrame(() => {
        if (!didRestoreRef.current) {
          listRef.current?.scrollToOffset({
            offset: session.scrollOffsetY ?? 0,
            animated: false,
          });

          didRestoreRef.current = true;
        }
      });

      return () => {};
    }, [])
  );

  const hasActiveFilters =
    !!search.trim() ||
    activeCategory !== "All" ||
    !!selectedCityId ||
    !!selectedAreaId;

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const startListing = useCallback(() => {
    router.push({
      pathname: "/(owner)/onboarding/signin",
      params: { returnTo: "/(owner)/start" },
    } as any);
  }, []);

  const onListScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const y = event.nativeEvent.contentOffset.y;

      scrollOffsetRef.current = y;
      updateDirectoryResultsScrollOffset(y);
    },
    []
  );

  const runSmartSearch = useCallback(async (rawValue: string) => {
    const rawSearch = rawValue.trim();
    const normalizedQuery = normalizeText(rawSearch);

    didRestoreRef.current = false;

    if (!rawSearch || !canTriggerSmartSearch(rawSearch)) {
      setSmartSearch(null);
      return;
    }

    const cached = smartSearchCacheRef.current.get(normalizedQuery);
    if (cached) {
      setSmartSearch(cached);
      return;
    }

    if (lastSmartSearchParsedQueryRef.current === normalizedQuery) {
      return;
    }

    const requestId = smartSearchRequestIdRef.current + 1;
    smartSearchRequestIdRef.current = requestId;

    const intent = await parseSearchIntent(rawSearch);

    if (smartSearchRequestIdRef.current !== requestId) return;
    if (normalizeText(searchInputRef.current) !== normalizedQuery) return;

    if (!intent) {
      setSmartSearch(null);
      return;
    }

    const nextSmartSearch = {
      normalizedQuery,
      directoryQuery: buildSmartDirectorySearchQuery(rawSearch, intent),
      intent,
    };

    smartSearchCacheRef.current.set(normalizedQuery, nextSmartSearch);
    lastSmartSearchParsedQueryRef.current = normalizedQuery;

    setSmartSearch(nextSmartSearch);
  }, []);

  const submitSmartSearch = useCallback(() => {
    const currentSearch = searchInputRef.current;

    setSubmittedSearch(currentSearch);
    runSmartSearch(currentSearch);
  }, [runSmartSearch]);

  useEffect(() => {
    const rawSearch = smartDebouncedSearch.trim();

    if (!rawSearch || !canTriggerSmartSearch(rawSearch)) return;

    runSmartSearch(rawSearch);
  }, [smartDebouncedSearch, runSmartSearch]);

  const openBusiness = useCallback(
    (business: Business) => {
      if (!business.slug) return;

      const index = filteredSlugs.indexOf(business.slug);

      setDirectoryResultsSession({
        slugs: filteredSlugs,
        selectedIndex: index >= 0 ? index : 0,
        scrollOffsetY: scrollOffsetRef.current,
        filters: {
          search,
          activeCategory,
          selectedCityId,
          selectedAreaId,
        },
        updatedAt: Date.now(),
      });

      router.push({
        pathname: "/p/[slug]",
        params: {
          slug: business.slug,
        },
      });
    },
    [filteredSlugs, search, activeCategory, selectedCityId, selectedAreaId]
  );

  const renderItem = useCallback(
    ({ item }: { item: Business }) => (
      <DirectoryCard business={item} onOpen={openBusiness} />
    ),
    [openBusiness]
  );

  function clearFilters() {
    smartSearchRequestIdRef.current += 1;
    searchInputRef.current = "";

    setSearch("");
    setSmartSearch(null);
    setSubmittedSearch(null);
    setSelectedCityId(null);
    setSelectedAreaId(null);
    setActiveCategory("All");
    setAreasForCity([]);
    setAllCities([]);

    scrollOffsetRef.current = 0;

    listRef.current?.scrollToOffset({
      offset: 0,
      animated: false,
    });
  }

  function onSelectCity(cityId: string | null) {
    setSelectedCityId(cityId);
    setSelectedAreaId(null);
    setAreasForCity([]);

    scrollOffsetRef.current = 0;

    listRef.current?.scrollToOffset({
      offset: 0,
      animated: false,
    });
  }

  function onSelectArea(areaId: string | null) {
    setSelectedAreaId(areaId);

    scrollOffsetRef.current = 0;

    listRef.current?.scrollToOffset({
      offset: 0,
      animated: false,
    });
  }

  const directoryContent = (
    <>
      <View style={styles.headerRow}>
        <Text
          style={styles.title}
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.9}
        >
          Soko Grid Marketplace
        </Text>

        <View style={styles.headerActions}>
          <Pressable onPress={() => setMenuOpen(true)} style={styles.menuBtn}>
            <Text style={styles.menuBtnText}>Menu</Text>
          </Pressable>
        </View>
      </View>

      {!useDesktopLayout ? (
        <Text style={styles.directoryIntro}>{DIRECTORY_MOBILE_INTRO}</Text>
      ) : null}

      <View style={styles.searchWrap}>
        <TextInput
          value={search}
          onChangeText={(value) => {
            smartSearchRequestIdRef.current += 1;
            searchInputRef.current = value;

            setSearch(value);
            setSubmittedSearch(null);

            if (!value.trim()) {
              setSmartSearch(null);
              setSelectedCityId(null);
              setSelectedAreaId(null);
              setAllCities([]);
              setAreasForCity([]);
            }

            didRestoreRef.current = false;
          }}
          placeholder="Search by business, category, service, city, area..."
          placeholderTextColor="#9ca3af"
          style={styles.search}
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType="search"
          onSubmitEditing={submitSmartSearch}
          clearButtonMode="while-editing"
        />

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Apply smart search"
          onPress={submitSmartSearch}
          style={({ pressed }) => [
            styles.searchIconButton,
            pressed ? styles.pressed : null,
          ]}
        >
          <SearchIcon size={18} color="#374151" strokeWidth={2.4} />
        </Pressable>
      </View>

      {smartSearchApplied ? (
        <Text style={styles.smartSearchHint}>Smart search applied</Text>
      ) : null}

      {showCityFilter ? (
        <InlineFilterRow
          label="City"
          allLabel="All cities"
          items={availableCities}
          selectedId={selectedCityId}
          onSelect={onSelectCity}
          loading={loadingCities}
          emptyText="No cities found for this search."
        />
      ) : null}

      {showAreaFilter ? (
        <InlineFilterRow
          label="Area"
          allLabel="All areas"
          items={availableAreas}
          selectedId={selectedAreaId}
          onSelect={onSelectArea}
          loading={loadingAreas}
          emptyText="No areas found for this city."
        />
      ) : null}

      <View style={styles.categoryRow}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.pillsRow}
          style={styles.categoryScroll}
        >
          {categories.map((category) => {
            const active = activeCategory === category;

            return (
              <Pressable
                key={category}
                onPress={() => {
                  setActiveCategory(category);

                  scrollOffsetRef.current = 0;

                  listRef.current?.scrollToOffset({
                    offset: 0,
                    animated: false,
                  });
                }}
                style={[
                  styles.pill,
                  active ? styles.pillActive : styles.pillInactive,
                ]}
              >
                <Text
                  style={[
                    styles.pillText,
                    active ? styles.pillTextActive : styles.pillTextInactive,
                  ]}
                  numberOfLines={1}
                >
                  {category}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>

        {hasActiveFilters ? (
          <Pressable style={styles.clearBtn} onPress={clearFilters}>
            <Text style={styles.clearBtnText}>Clear filters</Text>
          </Pressable>
        ) : null}
      </View>

      <Text style={styles.footerHint}>
        Tip: Search first, then refine by city, then area.
      </Text>

      {loading ? (
        <ActivityIndicator style={styles.loader} />
      ) : (
        <FlatList
          ref={listRef}
          data={filtered}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          initialNumToRender={8}
          maxToRenderPerBatch={8}
          windowSize={7}
          removeClippedSubviews
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={[
            styles.listContent,
            { paddingBottom: listBottomPadding },
          ]}
          onScroll={onListScroll}
          scrollEventThrottle={16}
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <Text style={styles.emptyTitle}>
                No matching businesses here yet
              </Text>
              <Text style={styles.emptyText}>
                Try another search or be the first to add a business to Soko
                Grid.
              </Text>
              <Pressable
                accessibilityRole="button"
                onPress={startListing}
                style={({ pressed }) => [
                  styles.emptyCta,
                  pressed ? styles.pressed : null,
                ]}
              >
                <Text style={styles.emptyCtaText}>List Your Business</Text>
                <ArrowRight size={17} color="#ffffff" strokeWidth={2.5} />
              </Pressable>
            </View>
          }
        />
      )}

      <PublicMenu
        visible={menuOpen}
        onClose={closeMenu}
        returnTo="/directory"
      />
    </>
  );

  return (
    <View style={[styles.page, { paddingTop: insets.top + 12 }]}>
      {useDesktopLayout ? (
        <View style={styles.desktopShell}>
          <View style={styles.leftSection}>
            <ScrollView
              style={styles.leftSectionScroll}
              contentContainerStyle={[
                styles.leftSectionScrollContent,
                { paddingBottom: listBottomPadding },
              ]}
            >
              <ListYourBusinessSidebar onStartListing={startListing} />
            </ScrollView>
          </View>

          <View style={styles.desktopDirectorySection}>
            {directoryContent}
          </View>

          <View style={styles.rightSection}>
            <FeaturedBusinessesPlaceholder />
          </View>
        </View>
      ) : (
        <View style={styles.container}>{directoryContent}</View>
      )}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* Styles */
/* ------------------------------------------------------------------ */

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#f3f4f6",
    padding: 16,
  },
  container: {
    width: "100%",
    maxWidth: 980,
    alignSelf: "center",
    flex: 1,
  },
  desktopShell: {
    width: "100%",
    maxWidth: 1440,
    alignSelf: "center",
    flex: 1,
    flexDirection: "row",
    alignItems: "stretch",
    gap: 16,
  },
  leftSection: {
    width: 292,
    flexShrink: 0,
    minHeight: 0,
  },
  leftSectionScroll: {
    flex: 1,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
  },
  leftSectionScrollContent: {
    padding: 16,
  },
  desktopDirectorySection: {
    flex: 1,
    minWidth: 0,
  },
  rightSection: {
    width: 246,
    flexShrink: 0,
  },
  sideCard: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 16,
  },
  sideCardTitle: {
    fontSize: 19,
    lineHeight: 25,
    fontWeight: "900",
    color: "#111827",
  },
  featuredTitle: {
    fontSize: 18,
    lineHeight: 24,
    fontWeight: "900",
    color: "#111827",
  },
  sideCardText: {
    marginTop: 9,
    fontSize: 13,
    lineHeight: 20,
    fontWeight: "700",
    color: "#4b5563",
  },
  sidebarSection: {
    marginTop: 16,
  },
  sidebarSectionTitle: {
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "900",
    color: "#111827",
  },
  compactCardList: {
    marginTop: 9,
    gap: 8,
  },
  compactFeatureItem: {
    minHeight: 34,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#f9fafb",
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  featureDot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    backgroundColor: "#111827",
    flexShrink: 0,
  },
  compactItemText: {
    flex: 1,
    minWidth: 0,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "900",
    color: "#111827",
  },
  checklist: {
    marginTop: 9,
    gap: 7,
  },
  checklistItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  checkIndicator: {
    width: 15,
    height: 15,
    borderRadius: 999,
    backgroundColor: "#dcfce7",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 2,
  },
  checkIndicatorInner: {
    width: 6,
    height: 6,
    borderRadius: 999,
    backgroundColor: "#16a34a",
  },
  checklistText: {
    flex: 1,
    minWidth: 0,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "800",
    color: "#374151",
  },
  startListingButton: {
    minHeight: 42,
    borderRadius: 12,
    backgroundColor: "#111827",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
    paddingHorizontal: 14,
    marginTop: 14,
  },
  startListingButtonText: {
    fontSize: 13,
    fontWeight: "900",
    color: "#ffffff",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
    gap: 10,
  },
  title: {
    flex: 1,
    minWidth: 0,
    fontSize: 22,
    lineHeight: 28,
    fontWeight: "900",
    color: "#111827",
    marginRight: 8,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },
  menuBtn: {
    minHeight: 36,
    paddingHorizontal: 13,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
  },
  menuBtnText: {
    fontSize: 12,
    fontWeight: "900",
    color: "#111827",
  },
  directoryIntro: {
    marginBottom: 10,
    fontSize: 13,
    lineHeight: 19,
    fontWeight: "600",
    color: "#6b7280",
  },
  searchWrap: {
    width: "100%",
    height: 46,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  search: {
    flex: 1,
    minWidth: 0,
    height: "100%",
    paddingLeft: 14,
    paddingRight: 8,
    fontSize: 14,
    fontWeight: "700",
    color: "#111827",
  },
  searchIconButton: {
    width: 44,
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    borderTopRightRadius: 14,
    borderBottomRightRadius: 14,
  },
  smartSearchHint: {
    marginTop: -4,
    marginBottom: 10,
    fontSize: 12,
    fontWeight: "800",
    color: "#166534",
  },

  inlineFilterBlock: {
    marginBottom: 10,
  },
  inlineFilterHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  inlineFilterScroll: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 24,
  },
  inlineFilterLabel: {
    fontSize: 13,
    fontWeight: "800",
    color: "#374151",
    marginRight: 10,
  },
  chip: {
    minHeight: 36,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    justifyContent: "center",
    marginRight: 8,
  },
  chipInactive: {
    backgroundColor: "#ffffff",
    borderColor: "#e5e7eb",
  },
  chipActive: {
    backgroundColor: "#111827",
    borderColor: "#111827",
  },
  chipText: {
    fontWeight: "800",
    fontSize: 13,
  },
  chipTextInactive: {
    color: "#111827",
  },
  chipTextActive: {
    color: "#ffffff",
  },
  emptyHint: {
    fontSize: 12,
    color: "#6b7280",
    fontWeight: "700",
    marginTop: 2,
  },

  categoryRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  categoryScroll: {
    flex: 1,
  },
  pillsRow: {
    paddingVertical: 2,
    paddingRight: 24,
    flexDirection: "row",
    alignItems: "center",
  },
  pill: {
    height: 34,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    justifyContent: "center",
    marginRight: 8,
  },
  pillInactive: {
    backgroundColor: "#ffffff",
    borderColor: "#e5e7eb",
  },
  pillActive: {
    backgroundColor: "#111827",
    borderColor: "#111827",
  },
  pillText: {
    fontWeight: "800",
    fontSize: 13,
  },
  pillTextInactive: {
    color: "#111827",
  },
  pillTextActive: {
    color: "#ffffff",
  },
  clearBtn: {
    marginLeft: 10,
    minHeight: 34,
    paddingHorizontal: 12,
    borderRadius: 999,
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    alignItems: "center",
    justifyContent: "center",
  },
  clearBtnText: {
    fontSize: 12,
    fontWeight: "800",
    color: "#374151",
  },

  cardBtn: {
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 16,
    padding: 12,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 8 },
    elevation: 2,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },
  logo: {
    width: 54,
    height: 54,
    borderRadius: 14,
    backgroundColor: "#e5e7eb",
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
    marginLeft: 12,
  },
  name: {
    fontSize: 16,
    fontWeight: "900",
    color: "#111827",
  },
  meta: {
    marginTop: 2,
    fontSize: 12,
    fontWeight: "800",
    color: "#374151",
  },
  addr: {
    marginTop: 2,
    fontSize: 12,
    fontWeight: "700",
    color: "#6b7280",
  },

  listContent: {
    paddingBottom: 24,
  },
  loader: {
    marginTop: 24,
  },
  footerHint: {
    marginBottom: 12,
    fontSize: 12,
    color: "#6b7280",
    fontWeight: "700",
  },
  emptyState: {
    marginTop: 16,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 10,
    backgroundColor: "#ffffff",
    padding: 18,
    alignItems: "flex-start",
  },
  emptyTitle: {
    fontSize: 16,
    lineHeight: 22,
    color: "#111827",
    fontWeight: "900",
  },
  emptyText: {
    marginTop: 6,
    fontSize: 13,
    lineHeight: 19,
    color: "#6b7280",
    fontWeight: "700",
  },
  emptyCta: {
    minHeight: 42,
    marginTop: 14,
    borderRadius: 8,
    backgroundColor: "#111827",
    paddingHorizontal: 15,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  emptyCtaText: {
    color: "#ffffff",
    fontSize: 13,
    fontWeight: "900",
  },
  pressed: {
    opacity: 0.82,
  },
});
